/**
 * Employer Onboarding Wizard
 *
 * Guided first-time setup experience for new employers:
 * - Step 1: Company profile & contacts
 * - Step 2: HRIS selection & connection
 * - Step 3: Import employees
 * - Step 4: Select programs to offer
 * - Step 5: Review & launch
 */

import React, { useState, useEffect } from 'react';
import { useAppTranslation } from '@/contexts/TranslationContext';
import {
  Building2,
  Users,
  Link2,
  Gift,
  CheckCircle2,
  ChevronRight,
  ChevronLeft,
  Loader2,
  Upload,
  FileSpreadsheet,
  Database,
  ArrowRight,
  Sparkles,
  Shield,
  AlertCircle,
  Check,
  X,
  RefreshCw,
  ExternalLink,
  Mail,
  Phone,
  User,
  Briefcase,
  Globe,
  MapPin,
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/contexts/AuthContext';
import { getWeaveinApiUrl } from '@/src/config';
import { authFetch } from '@/lib/authFetch';

interface WizardStep {
  id: string;
  number: number;
  title: string;
  description: string;
  icon: React.ElementType;
  isComplete: boolean;
  isActive: boolean;
}

interface CompanyProfile {
  companyName: string;
  industry: string;
  employeeCount: string;
  website: string;
  address: string;
  primaryContactName: string;
  primaryContactEmail: string;
  primaryContactPhone: string;
  hrContactName: string;
  hrContactEmail: string;
}

interface HRISConnection {
  provider: string;
  status: 'disconnected' | 'connecting' | 'connected' | 'error';
  lastSync?: string;
  employeeCount?: number;
}

interface ProgramSelection {
  id: number;
  name: string;
  description: string;
  category: string;
  selected: boolean;
}

interface EmployerOnboardingWizardProps {
  onComplete: () => void;
  onSkip: () => void;
  employerId?: number;
}

const HRIS_PROVIDERS = [
  { id: 'workday', name: 'Workday', logo: '🔷', description: 'Enterprise HR management' },
  { id: 'adp', name: 'ADP', logo: '🔶', description: 'Payroll & HR solutions' },
  { id: 'bamboohr', name: 'BambooHR', logo: '🟢', description: 'HR software for SMBs' },
  { id: 'gusto', name: 'Gusto', logo: '🟠', description: 'Payroll, benefits & HR' },
  { id: 'paylocity', name: 'Paylocity', logo: '🔵', description: 'Payroll & HCM' },
  { id: 'namely', name: 'Namely', logo: '🟣', description: 'All-in-one HR platform' },
  { id: 'rippling', name: 'Rippling', logo: '🟡', description: 'HR, IT & Finance' },
  { id: 'csv', name: 'CSV Upload', logo: '📄', description: 'Manual file upload' },
];

const INDUSTRIES = [
  'Healthcare',
  'Technology',
  'Manufacturing',
  'Retail',
  'Financial Services',
  'Professional Services',
  'Education',
  'Government',
  'Non-Profit',
  'Other',
];

export default function EmployerOnboardingWizard({
  onComplete,
  onSkip,
  employerId,
}: EmployerOnboardingWizardProps) {
  const { t } = useAppTranslation();
  const { token } = useAuth();
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Step 1: Company Profile
  const [companyProfile, setCompanyProfile] = useState<CompanyProfile>({
    companyName: '',
    industry: '',
    employeeCount: '',
    website: '',
    address: '',
    primaryContactName: '',
    primaryContactEmail: '',
    primaryContactPhone: '',
    hrContactName: '',
    hrContactEmail: '',
  });

  // Step 2: HRIS Connection
  const [selectedHRIS, setSelectedHRIS] = useState<string | null>(null);
  const [hrisConnection, setHrisConnection] = useState<HRISConnection>({
    provider: '',
    status: 'disconnected',
  });

  // Step 3: Employee Import
  const [importMethod, setImportMethod] = useState<'hris' | 'csv' | 'manual'>('hris');
  const [importedEmployees, setImportedEmployees] = useState<number>(0);
  const [importStatus, setImportStatus] = useState<'idle' | 'importing' | 'complete' | 'error'>('idle');

  // Step 4: Program Selection
  const [availablePrograms, setAvailablePrograms] = useState<ProgramSelection[]>([]);
  const [selectedPrograms, setSelectedPrograms] = useState<number[]>([]);

  // Step completion tracking
  const [stepCompletion, setStepCompletion] = useState({
    step1: false,
    step2: false,
    step3: false,
    step4: false,
  });

  // Fetch available programs
  useEffect(() => {
    const fetchPrograms = async () => {
      try {
        const response = await authFetch(getWeaveinApiUrl('/api/programs'));

        if (response.ok) {
          const programs = await response.json();
          setAvailablePrograms(
            programs.map((p: any) => ({
              id: p.id,
              name: p.name,
              description: p.description || '',
              category: p.category || 'General',
              selected: false,
            }))
          );
        }
      } catch (error) {
        console.error('Error fetching programs:', error);
      }
    };

    fetchPrograms();
  }, [token]);

  // Load existing employer data if available
  useEffect(() => {
    const loadEmployerData = async () => {
      if (!employerId) return;

      try {
        const response = await authFetch(getWeaveinApiUrl(`/api/employers/${employerId}`));

        if (response.ok) {
          const data = await response.json();
          setCompanyProfile(prev => ({
            ...prev,
            companyName: data.company_name || '',
            industry: data.industry || '',
            employeeCount: data.employee_count?.toString() || '',
            website: data.website || '',
            address: data.address || '',
          }));
        }
      } catch (error) {
        console.error('Error loading employer data:', error);
      }
    };

    loadEmployerData();
  }, [employerId, token]);

  const wizardSteps: WizardStep[] = [
    {
      id: 'company',
      number: 1,
      title: 'Company Profile',
      description: 'Basic company information',
      icon: Building2,
      isComplete: stepCompletion.step1,
      isActive: currentStep === 1,
    },
    {
      id: 'hris',
      number: 2,
      title: 'Connect HRIS',
      description: 'Link your HR system',
      icon: Link2,
      isComplete: stepCompletion.step2,
      isActive: currentStep === 2,
    },
    {
      id: 'employees',
      number: 3,
      title: 'Import Employees',
      description: 'Bring in your team',
      icon: Users,
      isComplete: stepCompletion.step3,
      isActive: currentStep === 3,
    },
    {
      id: 'programs',
      number: 4,
      title: 'Select Programs',
      description: 'Choose benefits to offer',
      icon: Gift,
      isComplete: stepCompletion.step4,
      isActive: currentStep === 4,
    },
    {
      id: 'review',
      number: 5,
      title: 'Review & Launch',
      description: 'Confirm and go live',
      icon: CheckCircle2,
      isComplete: false,
      isActive: currentStep === 5,
    },
  ];

  const handleProfileChange = (field: keyof CompanyProfile, value: string) => {
    setCompanyProfile(prev => ({ ...prev, [field]: value }));
  };

  const validateStep1 = () => {
    return (
      companyProfile.companyName.trim() !== '' &&
      companyProfile.industry !== '' &&
      companyProfile.primaryContactEmail.trim() !== ''
    );
  };

  const validateStep2 = () => {
    return selectedHRIS !== null || hrisConnection.status === 'connected';
  };

  const validateStep3 = () => {
    return importedEmployees > 0 || importStatus === 'complete';
  };

  const validateStep4 = () => {
    return selectedPrograms.length > 0;
  };

  const handleSaveStep1 = async () => {
    if (!validateStep1()) return;

    setSaving(true);
    try {
      // Save company profile
      const response = await authFetch(getWeaveinApiUrl(`/api/employers/${employerId}`), {
        method: 'PATCH',
        jsonBody: {
          company_name: companyProfile.companyName,
          industry: companyProfile.industry,
          employee_count: parseInt(companyProfile.employeeCount) || 0,
          website: companyProfile.website,
          address: companyProfile.address,
        }
      });

      if (response.ok) {
        setStepCompletion(prev => ({ ...prev, step1: true }));
        setCurrentStep(2);
      }
    } catch (error) {
      console.error('Error saving company profile:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleConnectHRIS = async (providerId: string) => {
    setSelectedHRIS(providerId);
    setHrisConnection({
      provider: providerId,
      status: 'connecting',
    });

    // Simulate connection process
    setTimeout(() => {
      if (providerId === 'csv') {
        setHrisConnection({
          provider: providerId,
          status: 'connected',
        });
        setImportMethod('csv');
      } else {
        // In production, this would redirect to OAuth flow
        setHrisConnection({
          provider: providerId,
          status: 'connected',
          lastSync: new Date().toISOString(),
          employeeCount: Math.floor(Math.random() * 500) + 50,
        });
        setImportMethod('hris');
      }
      setStepCompletion(prev => ({ ...prev, step2: true }));
    }, 2000);
  };

  const handleImportEmployees = async () => {
    setImportStatus('importing');

    // Simulate import process
    setTimeout(() => {
      const count = hrisConnection.employeeCount || Math.floor(Math.random() * 200) + 50;
      setImportedEmployees(count);
      setImportStatus('complete');
      setStepCompletion(prev => ({ ...prev, step3: true }));
    }, 3000);
  };

  const handleProgramToggle = (programId: number) => {
    setSelectedPrograms(prev =>
      prev.includes(programId)
        ? prev.filter(id => id !== programId)
        : [...prev, programId]
    );
  };

  const handleSaveStep4 = () => {
    if (selectedPrograms.length > 0) {
      setStepCompletion(prev => ({ ...prev, step4: true }));
      setCurrentStep(5);
    }
  };

  const handleLaunch = async () => {
    setLoading(true);
    try {
      // Save selected programs to employer
      await authFetch(getWeaveinApiUrl(`/api/employer-benefit-programs/employer/${employerId}/bulk`), {
        method: 'POST',
        jsonBody: {
          program_ids: selectedPrograms,
        }
      });

      // Mark onboarding as complete
      await authFetch(getWeaveinApiUrl(`/api/employers/${employerId}`), {
        method: 'PATCH',
        jsonBody: {
          onboarding_complete: true,
          onboarding_completed_at: new Date().toISOString(),
        }
      });

      // Store completion in localStorage
      localStorage.setItem(`employer_${employerId}_onboarding_complete`, 'true');

      onComplete();
    } catch (error) {
      console.error('Error completing onboarding:', error);
    } finally {
      setLoading(false);
    }
  };

  const progressPercentage = ((currentStep - 1) / (wizardSteps.length - 1)) * 100;

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-blue-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 p-4 sm:p-6 max-w-full overflow-hidden">
      {/* Header */}
      <div className="max-w-4xl mx-auto mb-8">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-gradient-to-br from-purple-600 to-blue-600 rounded-xl">
              <Sparkles className="h-8 w-8 text-white" />
            </div>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-gray-100">
                {t('employer.onboarding.welcomeTitle')}
              </h1>
              <p className="text-gray-600 dark:text-gray-400">
                {t('employer.onboarding.welcomeSubtitle')}
              </p>
            </div>
          </div>
          <Button variant="ghost" onClick={onSkip} className="text-gray-500">
            {t('employer.onboarding.skipForNow')}
          </Button>
        </div>

        {/* Progress Bar */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-gray-600">{t('employer.onboarding.setupProgress')}</span>
            <span className="text-sm font-medium text-purple-600">{Math.round(progressPercentage)}%</span>
          </div>
          <Progress value={progressPercentage} className="h-2" />
        </div>

        {/* Step Indicators */}
        <div className="flex justify-between mb-8">
          {wizardSteps.map((step, index) => {
            const Icon = step.icon;
            return (
              <div
                key={step.id}
                className={`flex flex-col items-center ${index < wizardSteps.length - 1 ? 'flex-1' : ''}`}
              >
                <div className="relative flex items-center w-full">
                  <div
                    className={`
                      w-10 h-10 rounded-full flex items-center justify-center z-10
                      ${step.isComplete
                        ? 'bg-green-500 text-white'
                        : step.isActive
                          ? 'bg-purple-600 text-white ring-4 ring-purple-200'
                          : 'bg-gray-200 text-gray-500 dark:bg-gray-700'
                      }
                    `}
                  >
                    {step.isComplete ? (
                      <Check className="w-5 h-5" />
                    ) : (
                      <Icon className="w-5 h-5" />
                    )}
                  </div>
                  {index < wizardSteps.length - 1 && (
                    <div
                      className={`
                        flex-1 h-1 mx-2
                        ${step.isComplete ? 'bg-green-500' : 'bg-gray-200 dark:bg-gray-700'}
                      `}
                    />
                  )}
                </div>
                <span className={`
                  mt-2 text-xs text-center hidden sm:block
                  ${step.isActive ? 'text-purple-600 font-medium' : 'text-gray-500'}
                `}>
                  {step.title}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Step Content */}
      <div className="max-w-4xl mx-auto">
        {/* Step 1: Company Profile */}
        {currentStep === 1 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building2 className="w-5 h-5 text-purple-600" />
                {t('employer.onboarding.companyProfile')}
              </CardTitle>
              <CardDescription>
                {t('employer.onboarding.companyProfileDesc')}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="companyName">{t('employer.onboarding.companyName')} *</Label>
                  <Input
                    id="companyName"
                    placeholder="Acme Corporation"
                    value={companyProfile.companyName}
                    onChange={(e) => handleProfileChange('companyName', e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="industry">{t('employer.onboarding.industry')} *</Label>
                  <select
                    id="industry"
                    className="w-full h-10 px-3 rounded-md border border-input bg-background"
                    value={companyProfile.industry}
                    onChange={(e) => handleProfileChange('industry', e.target.value)}
                  >
                    <option value="">{t('employer.onboarding.selectIndustry')}</option>
                    {INDUSTRIES.map((ind) => (
                      <option key={ind} value={ind}>{ind}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="employeeCount">{t('employer.onboarding.numberOfEmployees')}</Label>
                  <Input
                    id="employeeCount"
                    type="number"
                    placeholder="100"
                    value={companyProfile.employeeCount}
                    onChange={(e) => handleProfileChange('employeeCount', e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="website">{t('employer.onboarding.website')}</Label>
                  <Input
                    id="website"
                    placeholder="https://www.example.com"
                    value={companyProfile.website}
                    onChange={(e) => handleProfileChange('website', e.target.value)}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="address">{t('employer.onboarding.address')}</Label>
                <Input
                  id="address"
                  placeholder="123 Main St, City, State 12345"
                  value={companyProfile.address}
                  onChange={(e) => handleProfileChange('address', e.target.value)}
                />
              </div>

              <div className="border-t pt-4">
                <h3 className="font-medium mb-4 flex items-center gap-2">
                  <User className="w-4 h-4" />
                  {t('employer.onboarding.primaryContact')}
                </h3>
                <div className="grid sm:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="primaryContactName">{t('employer.onboarding.name')}</Label>
                    <Input
                      id="primaryContactName"
                      placeholder="John Doe"
                      value={companyProfile.primaryContactName}
                      onChange={(e) => handleProfileChange('primaryContactName', e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="primaryContactEmail">{t('employer.onboarding.email')} *</Label>
                    <Input
                      id="primaryContactEmail"
                      type="email"
                      placeholder="john@example.com"
                      value={companyProfile.primaryContactEmail}
                      onChange={(e) => handleProfileChange('primaryContactEmail', e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="primaryContactPhone">{t('employer.onboarding.phone')}</Label>
                    <Input
                      id="primaryContactPhone"
                      placeholder="(555) 123-4567"
                      value={companyProfile.primaryContactPhone}
                      onChange={(e) => handleProfileChange('primaryContactPhone', e.target.value)}
                    />
                  </div>
                </div>
              </div>
            </CardContent>
            <CardFooter className="flex justify-end">
              <Button
                onClick={handleSaveStep1}
                disabled={!validateStep1() || saving}
                className="bg-purple-600 hover:bg-purple-700"
              >
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    {t('employer.onboarding.saving')}
                  </>
                ) : (
                  <>
                    {t('employer.onboarding.continue')}
                    <ChevronRight className="w-4 h-4 ml-2" />
                  </>
                )}
              </Button>
            </CardFooter>
          </Card>
        )}

        {/* Step 2: HRIS Connection */}
        {currentStep === 2 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Link2 className="w-5 h-5 text-purple-600" />
                {t('employer.onboarding.connectHris')}
              </CardTitle>
              <CardDescription>
                {t('employer.onboarding.connectHrisDesc')}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {HRIS_PROVIDERS.map((provider) => (
                  <div
                    key={provider.id}
                    onClick={() => handleConnectHRIS(provider.id)}
                    className={`
                      p-4 rounded-lg border-2 cursor-pointer transition-all
                      ${selectedHRIS === provider.id
                        ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20'
                        : 'border-gray-200 hover:border-purple-300 dark:border-gray-700'
                      }
                    `}
                  >
                    <div className="text-3xl mb-2">{provider.logo}</div>
                    <h4 className="font-medium text-gray-900 dark:text-gray-100">
                      {provider.name}
                    </h4>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {provider.description}
                    </p>
                    {selectedHRIS === provider.id && hrisConnection.status === 'connecting' && (
                      <div className="flex items-center gap-2 mt-2 text-purple-600">
                        <Loader2 className="w-4 h-4 animate-spin" />
                        <span className="text-xs">{t('employer.onboarding.connecting')}</span>
                      </div>
                    )}
                    {selectedHRIS === provider.id && hrisConnection.status === 'connected' && (
                      <div className="flex items-center gap-2 mt-2 text-green-600">
                        <CheckCircle2 className="w-4 h-4" />
                        <span className="text-xs">{t('employer.onboarding.connected')}</span>
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {hrisConnection.status === 'connected' && hrisConnection.employeeCount && (
                <div className="mt-6 p-4 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
                  <div className="flex items-center gap-3">
                    <CheckCircle2 className="w-6 h-6 text-green-600" />
                    <div>
                      <h4 className="font-medium text-green-800 dark:text-green-200">
                        {t('employer.onboarding.successfullyConnected')}
                      </h4>
                      <p className="text-sm text-green-600 dark:text-green-400">
                        {t('employer.onboarding.foundEmployees', { count: hrisConnection.employeeCount })}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
            <CardFooter className="flex justify-between">
              <Button variant="outline" onClick={() => setCurrentStep(1)}>
                <ChevronLeft className="w-4 h-4 mr-2" />
                {t('employer.onboarding.back')}
              </Button>
              <Button
                onClick={() => setCurrentStep(3)}
                disabled={!validateStep2()}
                className="bg-purple-600 hover:bg-purple-700"
              >
                {t('employer.onboarding.continue')}
                <ChevronRight className="w-4 h-4 ml-2" />
              </Button>
            </CardFooter>
          </Card>
        )}

        {/* Step 3: Import Employees */}
        {currentStep === 3 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="w-5 h-5 text-purple-600" />
                {t('employer.onboarding.importEmployees')}
              </CardTitle>
              <CardDescription>
                {t('employer.onboarding.importEmployeesDesc')}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {importStatus === 'idle' && (
                <div className="space-y-4">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div
                      onClick={() => hrisConnection.status === 'connected' && setImportMethod('hris')}
                      className={`
                        p-6 rounded-lg border-2 cursor-pointer transition-all text-center
                        ${importMethod === 'hris'
                          ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20'
                          : hrisConnection.status === 'connected'
                            ? 'border-gray-200 hover:border-purple-300'
                            : 'border-gray-200 opacity-50 cursor-not-allowed'
                        }
                      `}
                    >
                      <Database className="w-12 h-12 mx-auto mb-3 text-purple-600" />
                      <h4 className="font-medium text-gray-900 dark:text-gray-100">
                        {t('employer.onboarding.importFromHris')}
                      </h4>
                      <p className="text-sm text-gray-500 mt-1">
                        {hrisConnection.employeeCount
                          ? t('employer.onboarding.employeesFound', { count: hrisConnection.employeeCount })
                          : t('employer.onboarding.connectHrisFirst')
                        }
                      </p>
                    </div>
                    <div
                      onClick={() => setImportMethod('csv')}
                      className={`
                        p-6 rounded-lg border-2 cursor-pointer transition-all text-center
                        ${importMethod === 'csv'
                          ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20'
                          : 'border-gray-200 hover:border-purple-300'
                        }
                      `}
                    >
                      <FileSpreadsheet className="w-12 h-12 mx-auto mb-3 text-blue-600" />
                      <h4 className="font-medium text-gray-900 dark:text-gray-100">
                        {t('employer.onboarding.uploadCsv')}
                      </h4>
                      <p className="text-sm text-gray-500 mt-1">
                        {t('employer.onboarding.importFromSpreadsheet')}
                      </p>
                    </div>
                  </div>

                  {importMethod === 'csv' && (
                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
                      <Upload className="w-12 h-12 mx-auto mb-3 text-gray-400" />
                      <p className="text-gray-600 mb-2">
                        {t('employer.onboarding.dragDropCsv')}
                      </p>
                      <Button variant="outline" size="sm">
                        {t('employer.onboarding.chooseFile')}
                      </Button>
                      <p className="text-xs text-gray-400 mt-2">
                        {t('employer.onboarding.supportedFormats')}
                      </p>
                    </div>
                  )}

                  <Button
                    onClick={handleImportEmployees}
                    className="w-full bg-purple-600 hover:bg-purple-700"
                  >
                    <Upload className="w-4 h-4 mr-2" />
                    {t('employer.onboarding.startImport')}
                  </Button>
                </div>
              )}

              {importStatus === 'importing' && (
                <div className="text-center py-12">
                  <Loader2 className="w-16 h-16 mx-auto mb-4 text-purple-600 animate-spin" />
                  <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
                    {t('employer.onboarding.importingEmployees')}
                  </h3>
                  <p className="text-gray-500">
                    {t('employer.onboarding.thisMayTakeMoments')}
                  </p>
                </div>
              )}

              {importStatus === 'complete' && (
                <div className="text-center py-12">
                  <div className="w-16 h-16 mx-auto mb-4 bg-green-100 rounded-full flex items-center justify-center">
                    <CheckCircle2 className="w-10 h-10 text-green-600" />
                  </div>
                  <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
                    {t('employer.onboarding.importComplete')}
                  </h3>
                  <p className="text-gray-500 mb-4">
                    {t('employer.onboarding.successfullyImported', { count: importedEmployees })}
                  </p>
                  <div className="grid grid-cols-3 gap-4 max-w-md mx-auto">
                    <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                      <div className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                        {importedEmployees}
                      </div>
                      <div className="text-xs text-gray-500">{t('employer.onboarding.total')}</div>
                    </div>
                    <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                      <div className="text-2xl font-bold text-green-600">
                        {importedEmployees}
                      </div>
                      <div className="text-xs text-gray-500">{t('employer.onboarding.imported')}</div>
                    </div>
                    <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                      <div className="text-2xl font-bold text-gray-400">0</div>
                      <div className="text-xs text-gray-500">{t('employer.onboarding.skipped')}</div>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
            <CardFooter className="flex justify-between">
              <Button variant="outline" onClick={() => setCurrentStep(2)}>
                <ChevronLeft className="w-4 h-4 mr-2" />
                {t('employer.onboarding.back')}
              </Button>
              <Button
                onClick={() => setCurrentStep(4)}
                disabled={!validateStep3()}
                className="bg-purple-600 hover:bg-purple-700"
              >
                {t('employer.onboarding.continue')}
                <ChevronRight className="w-4 h-4 ml-2" />
              </Button>
            </CardFooter>
          </Card>
        )}

        {/* Step 4: Program Selection */}
        {currentStep === 4 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Gift className="w-5 h-5 text-purple-600" />
                {t('employer.onboarding.selectPrograms')}
              </CardTitle>
              <CardDescription>
                {t('employer.onboarding.selectProgramsDesc')}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {availablePrograms.length > 0 ? (
                  availablePrograms.map((program) => (
                    <div
                      key={program.id}
                      onClick={() => handleProgramToggle(program.id)}
                      className={`
                        p-4 rounded-lg border-2 cursor-pointer transition-all
                        ${selectedPrograms.includes(program.id)
                          ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20'
                          : 'border-gray-200 hover:border-purple-300 dark:border-gray-700'
                        }
                      `}
                    >
                      <div className="flex items-start gap-3">
                        <div className={`
                          w-6 h-6 rounded-full border-2 flex items-center justify-center
                          ${selectedPrograms.includes(program.id)
                            ? 'border-purple-500 bg-purple-500'
                            : 'border-gray-300'
                          }
                        `}>
                          {selectedPrograms.includes(program.id) && (
                            <Check className="w-4 h-4 text-white" />
                          )}
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <h4 className="font-medium text-gray-900 dark:text-gray-100">
                              {program.name}
                            </h4>
                            <Badge variant="secondary" className="text-xs">
                              {program.category}
                            </Badge>
                          </div>
                          <p className="text-sm text-gray-500 mt-1">
                            {program.description || 'No description available'}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <Gift className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                    <p>{t('employer.onboarding.noProgramsAvailable')}</p>
                  </div>
                )}
              </div>

              {selectedPrograms.length > 0 && (
                <div className="mt-4 p-3 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
                  <p className="text-sm text-purple-700 dark:text-purple-300">
                    <strong>{selectedPrograms.length}</strong> program{selectedPrograms.length !== 1 ? 's' : ''} selected
                  </p>
                </div>
              )}
            </CardContent>
            <CardFooter className="flex justify-between">
              <Button variant="outline" onClick={() => setCurrentStep(3)}>
                <ChevronLeft className="w-4 h-4 mr-2" />
                {t('employer.onboarding.back')}
              </Button>
              <Button
                onClick={handleSaveStep4}
                disabled={!validateStep4()}
                className="bg-purple-600 hover:bg-purple-700"
              >
                {t('employer.onboarding.continue')}
                <ChevronRight className="w-4 h-4 ml-2" />
              </Button>
            </CardFooter>
          </Card>
        )}

        {/* Step 5: Review & Launch */}
        {currentStep === 5 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5 text-purple-600" />
                {t('employer.onboarding.reviewAndLaunch')}
              </CardTitle>
              <CardDescription>
                {t('employer.onboarding.reviewAndLaunchDesc')}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                {/* Summary Cards */}
                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                    <Building2 className="w-6 h-6 text-purple-600 mb-2" />
                    <h4 className="font-medium text-gray-900 dark:text-gray-100">
                      {companyProfile.companyName || 'Your Company'}
                    </h4>
                    <p className="text-sm text-gray-500">{companyProfile.industry}</p>
                  </div>
                  <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                    <Link2 className="w-6 h-6 text-blue-600 mb-2" />
                    <h4 className="font-medium text-gray-900 dark:text-gray-100">
                      {t('employer.onboarding.hrisConnected')}
                    </h4>
                    <p className="text-sm text-gray-500">
                      {HRIS_PROVIDERS.find(p => p.id === selectedHRIS)?.name || t('employer.onboarding.notConnected')}
                    </p>
                  </div>
                  <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                    <Users className="w-6 h-6 text-green-600 mb-2" />
                    <h4 className="font-medium text-gray-900 dark:text-gray-100">
                      {t('employer.onboarding.employeesCount', { count: importedEmployees })}
                    </h4>
                    <p className="text-sm text-gray-500">{t('employer.onboarding.readyToEnroll')}</p>
                  </div>
                  <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                    <Gift className="w-6 h-6 text-amber-600 mb-2" />
                    <h4 className="font-medium text-gray-900 dark:text-gray-100">
                      {t('employer.onboarding.programsCount', { count: selectedPrograms.length })}
                    </h4>
                    <p className="text-sm text-gray-500">{t('employer.onboarding.selectedToOffer')}</p>
                  </div>
                </div>

                {/* What's Next */}
                <div className="bg-gradient-to-r from-purple-50 to-blue-50 dark:from-purple-900/20 dark:to-blue-900/20 rounded-lg p-6">
                  <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center gap-2">
                    <Sparkles className="w-5 h-5 text-purple-600" />
                    {t('employer.onboarding.whatHappensNext')}
                  </h3>
                  <ul className="space-y-3">
                    <li className="flex items-start gap-3">
                      <div className="w-6 h-6 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <span className="text-xs font-bold text-purple-600">1</span>
                      </div>
                      <div>
                        <p className="font-medium text-gray-900 dark:text-gray-100">{t('employer.onboarding.mlAnalysisBegins')}</p>
                        <p className="text-sm text-gray-500">{t('employer.onboarding.mlAnalysisDesc')}</p>
                      </div>
                    </li>
                    <li className="flex items-start gap-3">
                      <div className="w-6 h-6 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <span className="text-xs font-bold text-purple-600">2</span>
                      </div>
                      <div>
                        <p className="font-medium text-gray-900 dark:text-gray-100">{t('employer.onboarding.programOffersSent')}</p>
                        <p className="text-sm text-gray-500">{t('employer.onboarding.programOffersSentDesc')}</p>
                      </div>
                    </li>
                    <li className="flex items-start gap-3">
                      <div className="w-6 h-6 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <span className="text-xs font-bold text-purple-600">3</span>
                      </div>
                      <div>
                        <p className="font-medium text-gray-900 dark:text-gray-100">{t('employer.onboarding.trackAndMeasure')}</p>
                        <p className="text-sm text-gray-500">{t('employer.onboarding.trackAndMeasureDesc')}</p>
                      </div>
                    </li>
                  </ul>
                </div>
              </div>
            </CardContent>
            <CardFooter className="flex justify-between">
              <Button variant="outline" onClick={() => setCurrentStep(4)}>
                <ChevronLeft className="w-4 h-4 mr-2" />
                {t('employer.onboarding.back')}
              </Button>
              <Button
                onClick={handleLaunch}
                disabled={loading}
                className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    {t('employer.onboarding.launching')}
                  </>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4 mr-2" />
                    {t('employer.onboarding.launchBenefitsHub')}
                  </>
                )}
              </Button>
            </CardFooter>
          </Card>
        )}
      </div>
    </div>
  );
}
